import React, { useRef, useEffect } from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import * as PIXI from "pixi.js";
import JackpotsController from "./JackpotsController";
import { selectJackpotValues } from "../../../State/Slices/gameSettingsSlice";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import { useSlot } from "../../../Contexts/SlotRegistryContext";

const Jackpots = ({ scope = "base" }) => {
  const localControllerRef = useRef(null);
  const containerRef = useRef(null);
  const jackpotValues = useSelector(selectJackpotValues);
  const { jackpotsControllerRef, rcJackpotsControllerRef } = useReelRefs();

  // Derive registry slot name from scope prop
  const slotName = scope === "rc" ? "reelfront.rc.jackpots" : "reelfront.base.jackpots";
  const jackpotsSlotContainer = useSlot(slotName);

  // Initialize JackpotsController
  useEffect(() => {
    if (containerRef.current && !localControllerRef.current) {
      localControllerRef.current = new JackpotsController(
        containerRef.current,
      );
      // Store in the correct scoped ref
      if (scope === "rc") {
        rcJackpotsControllerRef.current = localControllerRef.current;
      } else {
        jackpotsControllerRef.current = localControllerRef.current;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inject the container into the ReelFrontController's jackpots slot via SlotRegistry
  useEffect(() => {
    if (!jackpotsSlotContainer || !containerRef.current) return;
    const node = containerRef.current;
    jackpotsSlotContainer.addChild(node);
    return () => {
      if (node.parent === jackpotsSlotContainer) {
        jackpotsSlotContainer.removeChild(node);
      }
    };
  }, [jackpotsSlotContainer]);

  // Initialize text once spine is ready, then update on value changes
  useEffect(() => {
    if (!localControllerRef.current || !jackpotValues) return;

    const ctrl = localControllerRef.current;

    // If spine isn't ready yet, wait for it
    // If spine or font isn't ready yet, wait for both
    if (!ctrl.spine || !PIXI.BitmapFont.available["font_primary_small"]) {
      const waitForReady = () => {
        if (ctrl.spine && PIXI.BitmapFont.available["font_primary_small"]) {
          ctrl.initializeJackpotTexts(jackpotValues);
        } else {
          requestAnimationFrame(waitForReady);
        }
      };
      requestAnimationFrame(waitForReady);
    } else {
      // Spine is ready — update or initialize texts
      if (Object.keys(ctrl.textInstances).length === 0) {
        ctrl.initializeJackpotTexts(jackpotValues);
      } else {
        ctrl.updateJackpotValues(jackpotValues);
      }
    }
  }, [jackpotValues]);

  return (
    <Container
      ref={containerRef}
      name="jackpots"
      sortableChildren
    />
  );
};

export default Jackpots;
